import React, { useState, useEffect } from "react";
import { Modal, Upload, message, UploadProps, Button, Spin, Select } from "antd";
import { DeleteOutlined, InboxOutlined } from "@ant-design/icons";
import type { RcFile } from "antd/es/upload";
import { GetUsuaris } from "../../../../dao/GetUsuaris";
import { GetUsuarisPractica } from "../../../../dao/jutge/GetUsuarisPractica";

const { Dragger } = Upload;
const { Option } = Select;

interface User {
    id: number;
    nom: string;
    cognoms: string;
}

interface FileWithUser {
    file: RcFile;
    relativePath: string;
    assignedUser?: User;
}

interface Props {
    visible: boolean;
    onClose: (updated: boolean) => void;
    id_practica: number;
}

import { PostJutgePracticaIdUsuariIdMulti } from "../../../../dao/jutge/PostJutgePracticaIdUsuariIdMulti";

const UploadFolderModal: React.FC<Props> = ({ visible, onClose, id_practica }) => {
    const [fileList, setFileList] = useState<FileWithUser[]>([]);
    const [uploading, setUploading] = useState(false);
    const [users, setUsers] = useState<User[]>([]);

    // 1️⃣ Obtener usuarios al montar o cuando se abra el modal
    useEffect(() => {
        if (visible) {
            (async () => {
                const data = await GetUsuarisPractica(id_practica + "");
                setUsers(data);
            })();
        }
    }, [visible]);

    // 2️⃣ Asignación automática cuando los usuarios estén cargados

    const normalize = (str: string) =>
        str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\s/g, "")
            .toLowerCase();

    // folder: carpeta del usuario
    const matchFolderToUser = (folder: string, user: User) => {
        const normalize = (str: string) =>
            str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s/g, "").toLowerCase();

        const folderNorm = normalize(folder);
        const firstName = normalize(user.nom);
        const firstSurname = user.cognoms ? normalize(user.cognoms.split(" ")[0]) : "";

        // Reglas de match
        if (firstSurname && folderNorm === firstSurname) return true;                 // "Casco"
        if (firstSurname && folderNorm === firstName.charAt(0) + firstSurname) return true; // "ARodriguez"
        if (firstSurname && folderNorm === firstName + firstSurname) return true;    // "AleixRodriguez"
        if (!firstSurname && folderNorm === firstName) return true;                   // solo nombre
        return false;
    };


    useEffect(() => {
        if (users.length === 0 || fileList.length === 0) return;

        setFileList(prev =>
            prev.map(f => {
                if (f.assignedUser) return f;

                const parts = f.relativePath.split("/");
                const folderUser = parts.length > 1 ? parts[1] : "";
                //console.log("Archivo:", f.relativePath, "-> Carpeta detectada:", folderUser);

                const assignedUser = users.find(u => matchFolderToUser(folderUser, u));

                //if (!assignedUser) console.log("❌ No se encontró usuario para", folderUser);
                //else console.log("✅ Usuario asignado:", assignedUser.nom, assignedUser.cognoms);

                return { ...f, assignedUser };
            })
        );
    }, [users, fileList]); // 🔹 Dependencias: users y fileList


    const propsUpload: UploadProps = {
        multiple: true,
        directory: true,
        beforeUpload: (file) => {
            const path = (file as any).webkitRelativePath || file.name;
            const lowerPath = path.toLowerCase();

            if (
                lowerPath.includes(".metadata") ||
                lowerPath.includes(".ds_store") ||
                lowerPath.includes("thumbs.db") ||
                (!lowerPath.endsWith(".java") && !lowerPath.endsWith(".jsp")) ||
                lowerPath.endsWith("module-info.java") ||
                lowerPath.includes("test")
            ) {
                return Upload.LIST_IGNORE;
            }

            return true;
        },
        onChange: (info) => {
            const newFiles: FileWithUser[] = info.fileList
                .map((f) => f.originFileObj as RcFile)
                .filter(Boolean)
                .map((f) => ({
                    file: f,
                    relativePath: (f as any).webkitRelativePath || f.name,
                    assignedUser: undefined, // se asignará automáticamente en el useEffect de users
                }));

            setFileList(newFiles);
        },
        onRemove: (file) => {
            setFileList((prev) => prev.filter((f) => f.file.uid !== file.uid));
        },
        showUploadList: false, // ocultamos la lista por defecto
        customRequest: () => {
            return; // evitar subida automática
        },
    };

    const handleUpload = async () => {
        if (fileList.length === 0) {
            message.warning("No hay archivos válidos para subir.");
            return;
        }

        if (fileList.some((f) => !f.assignedUser)) {
            message.warning("Todos los archivos deben tener un usuario asignado.");
            return;
        }

        setUploading(true);

        // Agrupar archivos por usuario
        const filesByUser = new Map<number, FileWithUser[]>();
        for (const f of fileList) {
            if (f.assignedUser) {
                const uid = f.assignedUser.id;
                if (!filesByUser.has(uid)) filesByUser.set(uid, []);
                filesByUser.get(uid)!.push(f);
            }
        }

        let successCount = 0;
        let failCount = 0;

        // Iterar por cada usuario y procesar su entrega
        for (const [userId, files] of Array.from(filesByUser)) {
            try {
                const uidsToSubmit: string[] = [];

                // 1. Subir cada archivo individualmente
                for (const f of files) {
                    const formData = new FormData();
                    formData.append('file', f.file);
                    formData.append('uid', f.file.uid); // Usar UID del archivo como en UploadCell

                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}:8061/jutge/practica/${id_practica}/usuaris/${userId}/upload`,
                        {
                            method: 'POST',
                            body: formData
                        }
                    );

                    if (!response.ok) throw new Error(`Falló subida de archivo para user ${userId}`);
                    uidsToSubmit.push(f.file.uid);
                }

                // 2. Crear el enviament (Submit Multi)
                await PostJutgePracticaIdUsuariIdMulti(id_practica + "", userId + "", uidsToSubmit);
                successCount++;

            } catch (err) {
                console.error(`Error procesando entrega para usuario ${userId}:`, err);
                failCount++;
            }
        }

        setUploading(false);

        if (failCount === 0) {
            message.success(`Se han procesado ${successCount} entregas correctamente.`);
            setFileList([]);
            onClose(true);
        } else {
            message.warning(`Proceso finalizado. Correctos: ${successCount}. Fallidos: ${failCount}. Revisa la consola.`);
            if (successCount > 0) onClose(true); // Cerrar si hubo al menos uno bien, para refrescar
        }
    };

    return (
        <Modal
            open={visible}
            title="Subir carpeta con entregas"
            onCancel={() => onClose(false)}
            footer={null}
            centered
            width="60%"
        >
            <Spin spinning={uploading} tip="Subiendo, por favor espere...">
                <Dragger {...propsUpload} style={{ marginBottom: 16 }}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Clicka o arrastra una carpeta con archivos '.java' o '.jsp'</p>
                    <p className="ant-upload-hint">
                        Se asignará automáticamente un usuario basado en la ruta. Puedes corregirlo si es necesario.
                    </p>
                </Dragger>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <Button
                        danger
                        size="small"
                        onClick={() => setFileList(prev => prev.filter(f => f.assignedUser))}
                        disabled={fileList.length === 0}
                    >
                        Eliminar no asignados
                    </Button>
                </div>

                {fileList.map((f, idx) => (
                    <div
                        key={f.file.uid}
                        style={{
                            marginBottom: 8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <span style={{ flex: 1, marginRight: 8 }}>
                            <b>{f.relativePath}</b>
                        </span>

                        <Select
                            style={{ width: 250, marginRight: 8 }}
                            placeholder={
                                f.assignedUser ? `${f.assignedUser.nom} ${f.assignedUser.cognoms}` : "Asignar usuario"
                            }
                            showSearch
                            filterOption={(input, option) => {
                                const text: any = option?.children;
                                let str = "";
                                if (Array.isArray(text)) {
                                    str = text.join("");
                                } else if (typeof text === "string") {
                                    str = text;
                                }
                                return str.toLowerCase().includes(input.toLowerCase());
                            }}
                            value={f.assignedUser ? f.assignedUser.id : undefined}
                            onChange={(value) => {
                                const user = users.find((u) => u.id === value);
                                setFileList((prev) => {
                                    const newList = [...prev];
                                    newList[idx] = { ...newList[idx], assignedUser: user };
                                    return newList;
                                });
                            }}
                        >
                            {users.map((u) => (
                                <Option key={u.id} value={u.id}>
                                    {u.nom} {u.cognoms}
                                </Option>
                            ))}
                        </Select>

                        <DeleteOutlined
                            style={{ color: "red", cursor: "pointer", fontSize: 18 }}
                            onClick={() => {
                                setFileList((prev) => prev.filter((_, i) => i !== idx));
                            }}
                        />
                    </div>
                ))}

                <Button
                    type="primary"
                    onClick={handleUpload}
                    disabled={fileList.length === 0 || uploading || fileList.some((f) => !f.assignedUser)}
                    loading={uploading}
                    block
                    style={{ marginTop: 16 }}
                >
                    Subir carpeta
                </Button>
            </Spin>
        </Modal>
    );
};

export default UploadFolderModal;
